import { useEffect, useState } from 'react'
import { MarkdownBody } from '@design-system/components/MarkdownBody'

import { ArtifactChromeActions } from '@/components/artifact-chrome/ArtifactChromeActions'
import { cn } from '@/lib/utils'
import { ArtifactTipView } from '@/lib/api'

import { artifactPeekClass } from './artifactPeek'
import { ArtifactMdEditor } from './ArtifactMdEditor'
import { useArtifactTipAutosave } from './useArtifactTipAutosave'

export interface ArtifactEditorShellAutosaveConfig {
  runToken: string
  stepId: string
  artifactIdentity: string
  machineBody: string
  stepIndex?: number
  stepKey?: string
  existingTip?: ArtifactTipView | null
  onSaved?: (tip: ArtifactTipView) => void
  /** Living thread token — enables `updateArtifact` writes. */
  threadToken?: string | null
  /** Living artifact publicId. */
  livingPublicId?: string | null
  /** Living DTO optimistic version (optional). */
  livingVersion?: number | null
}

export interface ArtifactEditorShellProps {
  /** Display / draft markdown (embeds as `{{plot:id}}` / `{{quote:id}}`). */
  value: string
  accessibleName: string
  canEdit?: boolean
  /** When true, TipTap is editable; otherwise markdown peek. */
  isEditing?: boolean
  onEdit?: () => void
  onDone?: () => void
  onCancel?: () => void
  onCopy?: () => void
  onExpand?: () => void
  onChange?: (next: string) => void
  /** When set, edits debounce into THE VAULT / living store while `isEditing`. */
  autosave?: ArtifactEditorShellAutosaveConfig
  peek?: boolean
  className?: string
  testIdPrefix?: string
  /**
   * THE LIVING lineage. When `false`, show “Derived” badge
   * (`data-testid="artifact-lineage-badge"`).
   */
  original?: boolean
}

/**
 * Shared GPT-style chrome + TipTap surface (THE CANVAS).
 * Twin-test mounts this first; other artifact types reuse the same shell.
 */
export function ArtifactEditorShell(props: ArtifactEditorShellProps) {
  const {
    value,
    accessibleName,
    canEdit = false,
    isEditing = false,
    onEdit,
    onDone,
    onCancel,
    onCopy,
    onExpand,
    onChange,
    autosave,
    peek = false,
    className,
    testIdPrefix = 'artifact-chrome',
    original = true,
  } = props

  const [draft, setDraft] = useState(value)
  const [hasCopied, setHasCopied] = useState(false)

  const autosaveEnabled = Boolean(
    isEditing &&
      autosave &&
      (autosave.runToken ||
        (autosave.threadToken && autosave.livingPublicId)),
  )

  const tipAutosave = useArtifactTipAutosave({
    runToken: autosave?.runToken ?? '',
    stepId: autosave?.stepId ?? '',
    artifactIdentity: autosave?.artifactIdentity ?? '',
    machineBody: autosave?.machineBody ?? value,
    stepIndex: autosave?.stepIndex,
    stepKey: autosave?.stepKey,
    initialTip: autosave?.existingTip ?? null,
    enabled: autosaveEnabled,
    onSaved: autosave?.onSaved,
    threadToken: autosave?.threadToken,
    livingPublicId: autosave?.livingPublicId,
    livingVersion: autosave?.livingVersion,
  })

  useEffect(() => {
    if (!isEditing) {
      setDraft(value)
    }
  }, [isEditing, value])

  function handleChange(next: string) {
    setDraft(next)
    onChange?.(next)
    if (autosave && next !== (autosave.machineBody ?? value)) {
      tipAutosave.scheduleSave(next)
    }
  }

  async function handleDone() {
    await tipAutosave.flush()
    onDone?.()
  }

  function handleCancel() {
    setDraft(value)
    onChange?.(value)
    onCancel?.()
  }

  function handleRevert() {
    const original = autosave?.machineBody ?? value
    setDraft(original)
    onChange?.(original)
    if (autosave) {
      tipAutosave.scheduleSave(original)
    }
  }

  async function handleCopyClick() {
    if (onCopy) {
      onCopy()
      return
    }
    if (!navigator.clipboard?.writeText) {
      return
    }
    try {
      await navigator.clipboard.writeText(isEditing ? draft : value)
      setHasCopied(true)
      window.setTimeout(() => setHasCopied(false), 1500)
    } catch {
      setHasCopied(false)
    }
  }

  const staleBanner = tipAutosave.isStale ? (
    <div
      className="mb-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-foreground"
      data-testid="artifact-editor-shell-stale"
      role="status"
    >
      <span>A newer version was saved elsewhere. Your draft was kept.</span>
      <button
        type="button"
        className="ml-2 underline"
        data-testid="artifact-editor-shell-stale-dismiss"
        onClick={() => tipAutosave.clearStale()}
      >
        Dismiss
      </button>
    </div>
  ) : null

  const chrome = (
    <ArtifactChromeActions
      accessibleName={accessibleName}
      hasCopied={hasCopied}
      canEdit={canEdit}
      isEditing={isEditing}
      onEdit={canEdit && !isEditing ? onEdit : undefined}
      onDone={isEditing ? handleDone : undefined}
      onCancel={isEditing ? handleCancel : undefined}
      onRevert={
        isEditing && autosave && draft !== autosave.machineBody
          ? handleRevert
          : undefined
      }
      onCopy={handleCopyClick}
      onExpand={onExpand}
      testIdPrefix={testIdPrefix}
    />
  )

  const body = isEditing ? (
    <ArtifactMdEditor
      value={draft}
      onChange={handleChange}
      editable
      className="mt-2"
    />
  ) : (
    <div
      className={cn(
        'mt-2',
        peek ? artifactPeekClass(value) : undefined,
      )}
      data-testid="artifact-editor-shell-preview"
    >
      <MarkdownBody content={value} density="thread" />
      {peek ? (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-muted/30 to-transparent"
          aria-hidden
        />
      ) : null}
    </div>
  )

  return (
    <div className={className} data-testid="artifact-editor-shell">
      <div className="flex items-center justify-end gap-2">
        {original === false ? (
          <span
            className="rounded border border-border/70 bg-muted/50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
            data-testid="artifact-lineage-badge"
          >
            Derived
          </span>
        ) : null}
        {chrome}
      </div>
      {staleBanner}
      {body}
    </div>
  )
}
